import { useContext, useEffect, useRef, useState } from 'react';
import type { SidePanelContextColumnsPropsType } from '../../SidePanelModelsProvider';
import { SidePanelProviderContext } from '../../SidePanelModelsProvider';
import { v4 as uuidv4 } from 'uuid';
import type { GridColumnResizeParams } from '@mui/x-data-grid-pro';
import { MIN_COLUMN_WIDTH } from '../../../../GridDndEditor/Block/Table/defaultData.tsx';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import type { TableColumnType, TableRowType, TableType } from '../../../../grid/reduxStore/table.types';
import { TableTypeIdentifier, ColumnTypeIdentifier, RowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { useTableContentUndoRedo } from '../../../../hooks/useTableContentUndoRedo.ts';
import { defaultColumnMeta } from '../../../../GridDndEditor/Block/Table/variables';
import { redistributeColumnWidthsProportionally } from '../../../../GridDndEditor/Block/Table/redistributeColumnWidthsProportionally';
import { TableColumnAddPositions, TableSettingsTypes } from '../types';
import { updateGridTableBlockState } from '../../../../grid/reduxStore/editorSlice';
import { useTableManipulation } from '../../../../GridDndEditor/Block/Table/useTableManipulation';
import { EditorDefaultSidePanelType, setActiveSidePanel } from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import { openNotification } from 'components/notification';
import { useTranslation } from 'react-i18next';
import { useNewBlockDeletedHandler } from '../../../../hooks/useNewBlockDeletedHandler.ts';
import { useTableTotals } from '../../../../GridDndEditor/Block/Table/useTableTotals';
import { clearRowDiscountsIfNoPriceColumn } from 'components/editor/GridDndEditor/Block/Table/tableUtils/clearRowDiscounts';

const defaultAddedColumnInPx = 120;
type GetEmptyColumnType = {
  isDuplicated?: boolean;
};
export const getEmptyColumn = ({ isDuplicated = false }: GetEmptyColumnType): TableColumnType => {
  if (isDuplicated) {
    return { ...defaultColumnMeta, field: uuidv4() };
  } else {
    return { ...defaultColumnMeta, field: uuidv4(), width: defaultAddedColumnInPx };
  }
};

const DUPLICATABLE_COLUMNS = [ColumnTypeIdentifier.MULTIPLIER];

export const useTableColumnManipulation = () => {
  const { toggledTableSettingsPanel, setToggledTableSettingsPanel } = useContext(
    SidePanelProviderContext
  ) as SidePanelContextColumnsPropsType;
  const dispatch = useAppDispatch();
  const { selectedBlockIdByWrapper, selectedSectionId: sectionId } = useContext(SelectionContext);
  const deleteHandler = useNewBlockDeletedHandler();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const { getTableData } = useTableManipulation();
  const getTableTotals = useTableTotals();
  const [isPriceColumnExists, setIsPriceColumnExists] = useState<boolean>(true);
  const [isSubtotalColumnExists, setIsSubtotalColumnExists] = useState<boolean>(true);
  const preResizeContentRef = useRef<TableType | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper) return;
    if (!sectionId) throw new Error('Section id cannot be empty');

    const tableContentData = getTableData(selectedBlockIdByWrapper, sectionId);
    if (!tableContentData) return;

    const tableColumns = tableContentData.columns;
    const isPriceColumnExists = tableColumns.some((column) => column.columnType === ColumnTypeIdentifier.PRICE);
    const isSubtotalColumnExists = tableColumns.some((column) => column.columnType === ColumnTypeIdentifier.SUBTOTAL);
    setIsPriceColumnExists(isPriceColumnExists);
    setIsSubtotalColumnExists(isSubtotalColumnExists);
  }, [toggledTableSettingsPanel, sectionId, selectedBlockIdByWrapper]);

  const updateTableState = (blockId: string, tableData: TableType) => {
    if (!sectionId) throw new Error('Section id cannot be empty');
    const payload = {
      sectionId: sectionId,
      blockId: blockId,
      contentTable: tableData,
    };

    dispatch(updateGridTableBlockState(payload));
  };

  const addColumnAtIndexPosition = (
    allColumns: TableColumnType[],
    newColumnData: TableColumnType,
    positionIndex: number
  ): TableColumnType[] => {
    const updatedColumns: TableColumnType[] = [];
    for (let index = 0; index < allColumns.length; index++) {
      if (positionIndex === index) {
        updatedColumns.push(newColumnData);
      }
      updatedColumns.push({ ...allColumns[index] });
    }

    if (positionIndex === updatedColumns.length) {
      updatedColumns.push(newColumnData);
    }

    return updatedColumns;
  };

  const deleteColumnFieldFromRows = (allRows: TableRowType[], columnFieldId: string): TableRowType[] => {
    return allRows.map((row) => {
      const rowToBeUpdated = { ...row };
      delete rowToBeUpdated[columnFieldId];
      return rowToBeUpdated;
    });
  };

  type DeleteColumnAtIndexPositionType = {
    updatedColumns: TableColumnType[];
    updatedRows: TableRowType[];
  };

  const deleteColumnAtIndexPosition = (
    allColumns: TableColumnType[],
    allRows: TableRowType[],
    positionIndex: number,
    isShowTotalEnabled: boolean
  ): DeleteColumnAtIndexPositionType => {
    const columnFieldId = allColumns[positionIndex].field;
    const isSubtotalColumn = allColumns[positionIndex].columnType === ColumnTypeIdentifier.SUBTOTAL;
    const updatedRows = deleteColumnFieldFromRows(allRows, columnFieldId);
    if (isSubtotalColumn && isShowTotalEnabled) {
      openNotification({
        type: 'warning',
        title: t('document.grid.table.errors.calculations_are_based_on_subtotals.title'),
        description: t('document.grid.table.errors.calculations_are_based_on_subtotals.desc'),
        testId: 'remove-subtotal-column-warning',
      });
    }
    const totalWidthBeforeDeletion = allColumns.reduce(
      (sum, column) => (column.columnType === ColumnTypeIdentifier.HANDLER ? sum : sum + (column.width ?? 0)),
      0
    );
    const remainingColumns = allColumns.filter((_, index) => index !== positionIndex);
    const updatedColumns = redistributeColumnWidthsProportionally(remainingColumns, totalWidthBeforeDeletion);

    return { updatedColumns, updatedRows };
  };

  const setColumnFocus = (columnField: string) => {
    if (!toggledTableSettingsPanel) return;

    const tableCallbackDetails = toggledTableSettingsPanel.tableApi?.tableCallbackDetails;
    if (!tableCallbackDetails?.api?.setColumnHeaderFocus) return;

    tableCallbackDetails.api.setColumnHeaderFocus(columnField);
    dispatch(setActiveSidePanel({ type: TableSettingsTypes.TABLE_COLUMNS }));
    setToggledTableSettingsPanel({
      tableApi: {
        selectedModel: tableCallbackDetails.api.getColumnHeaderParams(columnField),
        tableCallbackDetails: tableCallbackDetails,
      },
    });
  };

  const handleColumnAdd = async (position: TableColumnAddPositions, newColumn: TableColumnType = getEmptyColumn({})) => {
    if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper) return;

    if (!sectionId) throw new Error('Section id cannot be empty');
    const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;

    const allColumns = tableData.columns;
    const allRows = [...tableData.rows];
    const footerRowElement = tableData.rows.find((row) => row.rowType === RowTypesIdentifier.FOOTER);

    const columnSelectedModel = toggledTableSettingsPanel.tableApi.selectedModel;
    const selectedColumnIndex = allColumns.findIndex((column) => column.field === columnSelectedModel.field);
    const indexToPlaceNewColumn = selectedColumnIndex + position;

    const updatedColumns = addColumnAtIndexPosition(allColumns, newColumn, indexToPlaceNewColumn);

    // A PRICE column takes over subtotal calculation: manual overrides (and stale
    // subtotal values) must be cleared so the engine/legacy calculation wins.
    const isAddingPriceColumn = newColumn.columnType === ColumnTypeIdentifier.PRICE;
    const subtotalField = updatedColumns.find((column) => column.columnType === ColumnTypeIdentifier.SUBTOTAL)?.field;

    const updatedRows = allRows.map((row) => {
      if (row.rowType === RowTypesIdentifier.FOOTER && footerRowElement) {
        const firstColumnFooterData = footerRowElement[tableData.columns[1].field];

        return { ...row, [updatedColumns[1].field]: firstColumnFooterData };
      } else {
        return {
          ...row,
          [newColumn.field]: `<p data-table-style="table_${row.rowType.toLowerCase()}_row"></p>`,
          ...(isAddingPriceColumn && { manualSubtotalOverride: null, ...(subtotalField && { [subtotalField]: '' }) }),
        };
      }
    });
    const { totalRows, metadata } = getTableTotals(updatedRows, updatedColumns, tableData);
    const nextTableData: TableType = {
      ...tableData,
      rows: updatedRows,
      columns: updatedColumns,
      totalRows,
      metadata,
    };

    handleTableContentChanged(selectedBlockIdByWrapper, sectionId, tableData, nextTableData);

    const newColumnField = updatedColumns[indexToPlaceNewColumn].field;
    setColumnFocus(newColumnField);
  };

  const getUpdatedRowsWithDuplicatedColumnField = (allRows: TableRowType[], columnFieldId: string, columnFieldToDuplicate: string) => {
    return allRows.map((row) => ({ ...row, [columnFieldId]: row[columnFieldToDuplicate] }));
  };

  const handleColumnDuplication = async () => {
    if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper) return;
    if (!sectionId) throw new Error('Section id cannot be empty');
    const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;

    const columnSelectedModel = toggledTableSettingsPanel.tableApi.selectedModel;
    const allRows = [...tableData.rows];
    const allColumns = [...tableData.columns];

    const selectedColumnIndex = allColumns.findIndex((column) => column.field === columnSelectedModel.field);
    if (selectedColumnIndex === -1) return;

    const selectedColumn = allColumns[selectedColumnIndex];

    const newColumnDefaultData = getEmptyColumn({ isDuplicated: true });
    if (selectedColumn.columnType && DUPLICATABLE_COLUMNS.includes(selectedColumn.columnType as ColumnTypeIdentifier)) {
      newColumnDefaultData.columnType = selectedColumn.columnType as ColumnTypeIdentifier;
      newColumnDefaultData.headerName = selectedColumn.headerName;
    }
    const columnFieldId = newColumnDefaultData.field;
    const updatedColumns = addColumnAtIndexPosition(
      allColumns,
      { ...allColumns[selectedColumnIndex], ...newColumnDefaultData },
      selectedColumnIndex + TableColumnAddPositions.AFTER
    );
    const updatedRows = getUpdatedRowsWithDuplicatedColumnField(allRows, columnFieldId, allColumns[selectedColumnIndex].field);
    const { totalRows, metadata } = getTableTotals(updatedRows, updatedColumns, tableData);
    const nextTableData: TableType = {
      ...tableData,
      rows: updatedRows,
      columns: updatedColumns,
      totalRows,
      metadata,
    };

    handleTableContentChanged(selectedBlockIdByWrapper, sectionId, tableData, nextTableData);
    setColumnFocus(columnFieldId);
  };

  const deleteTableBlockAndHideTableSettingsPanel = async (blockId: string) => {
    if (!sectionId) throw new Error('Section id cannot be empty');
    await deleteHandler(sectionId, blockId);
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
    setToggledTableSettingsPanel(null);
  };

  const handleColumnDeletion = async () => {
    if (!toggledTableSettingsPanel || !selectedBlockIdByWrapper) return;
    if (!sectionId) throw new Error('Section id cannot be empty');
    const tableData = getTableData(selectedBlockIdByWrapper, sectionId) as TableType;
    const isShowTotalEnabled = tableData?.metadata?.tableType === TableTypeIdentifier.PRICING_TABLE;
    const columnSelectedModel = toggledTableSettingsPanel.tableApi.selectedModel;

    const allRows = [...tableData.rows];
    const footerRowElement = allRows.find((row) => row.rowType === RowTypesIdentifier.FOOTER);
    const allColumns = [...tableData.columns];
    const selectedColumnIndex = allColumns.findIndex((column) => column.field === columnSelectedModel.field);
    const isOptionalColumnExists = allColumns[1].columnType === ColumnTypeIdentifier.OPTIONAL;
    const columnLengthToTest = isOptionalColumnExists ? 2 : 1;
    const columnIndexToTest = isOptionalColumnExists ? 1 : 0;
    const columnTypeToTest = isOptionalColumnExists ? ColumnTypeIdentifier.OPTIONAL : ColumnTypeIdentifier.HANDLER;

    const { updatedColumns, updatedRows } = deleteColumnAtIndexPosition(allColumns, allRows, selectedColumnIndex, isShowTotalEnabled);

    const isIdTheLastColumnLeft =
      updatedColumns.length === columnLengthToTest && updatedColumns[columnIndexToTest].columnType === columnTypeToTest;
    if (isIdTheLastColumnLeft) {
      await deleteTableBlockAndHideTableSettingsPanel(selectedBlockIdByWrapper);
      return;
    }
    const updatedRowsWithFooter = updatedRows.map((row) => {
      if (row.rowType === RowTypesIdentifier.FOOTER && footerRowElement) {
        const firstColumnFooterData = footerRowElement[tableData.columns[1].field];
        return { ...row, [updatedColumns[1].field]: firstColumnFooterData };
      } else {
        return row;
      }
    });
    // Removing the last PRICE column drops the discounts' calculation basis —
    // clear them (inverse of the PRICE-arrival rule that clears manual overrides).
    const rowsWithDiscountRule = clearRowDiscountsIfNoPriceColumn(updatedRowsWithFooter, updatedColumns);
    const { totalRows, metadata } = getTableTotals(rowsWithDiscountRule, updatedColumns, tableData);
    const nextTableData: TableType = {
      ...tableData,
      rows: rowsWithDiscountRule,
      columns: updatedColumns,
      totalRows,
      metadata,
    };
    handleTableContentChanged(selectedBlockIdByWrapper, sectionId, tableData, nextTableData);
    const columnToTheLeft = selectedColumnIndex > 1 ? selectedColumnIndex - 1 : selectedColumnIndex;
    setColumnFocus(updatedColumns[columnToTheLeft].field);
  };

  const updateColumnWidthStoreState = (blockId: string, updatedColumnField: string, updatedColumnWidth: number) => {
    if (!sectionId) throw new Error('Section id cannot be empty');
    const tableData = getTableData(blockId, sectionId);
    if (!tableData) return;
    const updatedColumns = tableData.columns.map((column) => {
      if (column.field === updatedColumnField) {
        return { ...column, width: updatedColumnWidth };
      }
      return { ...column };
    });
    updateTableState(blockId, { ...tableData, columns: updatedColumns });
  };

  const handleColumnResizeStart = (blockId: string) => {
    if (!sectionId) return;
    const tableData = getTableData(blockId, sectionId);
    if (tableData) {
      preResizeContentRef.current = structuredClone(tableData);
    }
  };

  const handleColumnResize = (blockId: string, gridColumnResizeParams: GridColumnResizeParams) => {
    if (!preResizeContentRef.current && sectionId) {
      const tableData = getTableData(blockId, sectionId);
      if (tableData) {
        preResizeContentRef.current = structuredClone(tableData);
      }
    }

    const resizedColumnField = gridColumnResizeParams.colDef.field;
    const resizedColumnNewWidth = Math.max(MIN_COLUMN_WIDTH, gridColumnResizeParams.width);
    updateColumnWidthStoreState(blockId, resizedColumnField, resizedColumnNewWidth);
  };

  const handleColumnResizingStop = async (blockId: string) => {
    if (!sectionId) throw new Error('Section id cannot be empty');

    const previousContent = preResizeContentRef.current;
    const currentContent = getTableData(blockId, sectionId) as TableType;
    preResizeContentRef.current = null;

    if (previousContent) {
      handleTableContentChanged(blockId, sectionId, previousContent, currentContent);
    }
  };

  return {
    handleColumnAdd,
    handleColumnDuplication,
    handleColumnDeletion,
    handleColumnResize,
    handleColumnResizingStop,
    updateColumnWidthStoreState,
    isPriceColumnExists,
    isSubtotalColumnExists,
    setIsPriceColumnExists,
    setIsSubtotalColumnExists,
    setColumnFocus,
    handleColumnResizeStart,
  };
};
